/**
 * Rhythmz — Generative Preset schema + curated originals
 * ──────────────────────────────────────────────────────
 * The ORIGINAL Rhythmz visual format (not Milkdrop). Describes a generative
 * composition: generator + palette + params + bindings. AI emits it, UI edits
 * it, engine renders it. Plain JSON → serializable/shareable.
 *
 * Validity-guaranteed: a known generator + clamped numbers always renders.
 */

export type GeneratorId = 'warp' | 'tunnel' | 'plasma' | 'reaction' | 'fractal' | 'particles';

export interface RGB { r: number; g: number; b: number; }  // 0..1

export interface GenParams {
  speed: number;        // 0..2
  warp: number;         // 0..1
  scale: number;        // 0.1..3
  complexity: number;   // 0..1
  intensity: number;    // 0..2
  rotation: number;     // -1..1
  hueShift: number;     // 0..1
}

export interface RhythmzPreset {
  id: string;
  name: string;
  generator: GeneratorId;
  palette: RGB[];
  params: GenParams;
  description?: string;
  ai?: boolean;
}

/* ─────────────── Curated original presets ───────────────
 * Hand-tuned per generator with distinct palettes. These are Rhythmz originals
 * — no borrowed Milkdrop art. The rail shows these by default. */

export const CURATED_PRESETS: RhythmzPreset[] = [
  {
    id: 'aurora-flow', name: 'Aurora Flow', generator: 'warp',
    palette: [{ r: 0.1, g: 0.9, b: 0.5 }, { r: 0.2, g: 0.5, b: 0.95 }, { r: 0.6, g: 0.3, b: 0.9 }],
    params: { speed: 0.7, warp: 0.6, scale: 1.5, complexity: 0.7, intensity: 1.1, rotation: 0.1, hueShift: 0.1 },
    description: 'Slow northern-lights drift',
  },
  {
    id: 'magma-bloom', name: 'Magma Bloom', generator: 'warp',
    palette: [{ r: 1, g: 0.3, b: 0.05 }, { r: 0.9, g: 0.1, b: 0.2 }, { r: 1, g: 0.8, b: 0.2 }],
    params: { speed: 1.3, warp: 0.8, scale: 1.1, complexity: 0.8, intensity: 1.3, rotation: 0.2, hueShift: 0 },
    description: 'Molten lava bloom',
  },
  {
    id: 'hyperspeed', name: 'Hyperspeed', generator: 'tunnel',
    palette: [{ r: 0.3, g: 0.1, b: 0.95 }, { r: 0.1, g: 0.8, b: 1 }, { r: 0.85, g: 0.3, b: 1 }],
    params: { speed: 1.6, warp: 0.5, scale: 1, complexity: 0.5, intensity: 1.2, rotation: 0, hueShift: 0.15 },
    description: 'Warp-speed kaleidoscope tunnel',
  },
  {
    id: 'solar-flare', name: 'Solar Flare', generator: 'plasma',
    palette: [{ r: 1, g: 0.85, b: 0.2 }, { r: 1, g: 0.4, b: 0.1 }, { r: 0.95, g: 0.1, b: 0.3 }],
    params: { speed: 1.2, warp: 0.4, scale: 1.4, complexity: 0.6, intensity: 1.2, rotation: 0, hueShift: 0.05 },
    description: 'Pulsing plasma sun',
  },
  {
    id: 'coral-growth', name: 'Coral Growth', generator: 'reaction',
    palette: [{ r: 0.95, g: 0.4, b: 0.7 }, { r: 0.2, g: 0.9, b: 0.6 }, { r: 0.1, g: 0.4, b: 0.8 }],
    params: { speed: 0.9, warp: 0.5, scale: 1.3, complexity: 0.7, intensity: 1.1, rotation: 0.3, hueShift: 0 },
    description: 'Living coral diffusion',
  },
  {
    id: 'deep-fractal', name: 'Deep Fractal', generator: 'fractal',
    palette: [{ r: 0.5, g: 0.1, b: 0.9 }, { r: 0.1, g: 0.9, b: 0.8 }, { r: 0.9, g: 0.9, b: 0.1 }],
    params: { speed: 1, warp: 0.6, scale: 1.2, complexity: 0.8, intensity: 1.2, rotation: 0.15, hueShift: 0.1 },
    description: 'Infinite-zoom fractal fold',
  },
  {
    id: 'starfield', name: 'Starfield', generator: 'particles',
    palette: [{ r: 0.9, g: 0.95, b: 1 }, { r: 0.6, g: 0.7, b: 1 }, { r: 1, g: 0.85, b: 0.6 }],
    params: { speed: 0.5, warp: 0.3, scale: 1, complexity: 0.9, intensity: 1.4, rotation: 0, hueShift: 0 },
    description: 'Drifting starfield dust',
  },
  {
    id: 'neon-storm', name: 'Neon Storm', generator: 'warp',
    palette: [{ r: 1, g: 0.1, b: 0.6 }, { r: 0.2, g: 0.8, b: 1 }, { r: 0.9, g: 0.2, b: 1 }],
    params: { speed: 1.5, warp: 0.85, scale: 1.3, complexity: 0.9, intensity: 1.3, rotation: 0.25, hueShift: 0.2 },
    description: 'Electric neon chaos',
  },
];

export const DEFAULT_PRESET = CURATED_PRESETS[0];

/* ─────────────── Validation / repair (trust boundaries: AI, URL) ─────────── */

const GENERATORS: GeneratorId[] = ['warp', 'tunnel', 'plasma', 'reaction', 'fractal', 'particles'];
const clamp = (n: number, lo: number, hi: number): number =>
  typeof n === 'number' && isFinite(n) ? Math.max(lo, Math.min(hi, n)) : (lo + hi) / 2;

function repairRGB(v: unknown): RGB {
  if (typeof v !== 'object' || v === null) return { r: 0.5, g: 0.5, b: 0.5 };
  const o = v as Record<string, unknown>;
  return { r: clamp(Number(o.r), 0, 1), g: clamp(Number(o.g), 0, 1), b: clamp(Number(o.b), 0, 1) };
}

export function repairPreset(raw: unknown): RhythmzPreset {
  const o = (typeof raw === 'object' && raw !== null ? raw : {}) as Record<string, unknown>;
  const generator = GENERATORS.includes(o.generator as GeneratorId) ? (o.generator as GeneratorId) : 'warp';
  const palRaw = Array.isArray(o.palette) ? o.palette : [];
  const palette = (palRaw.length >= 2 ? palRaw : [DEFAULT_PRESET.palette[0], DEFAULT_PRESET.palette[1]])
    .slice(0, 6).map(repairRGB);
  const p = (o.params && typeof o.params === 'object' ? o.params : {}) as Record<string, unknown>;
  const params: GenParams = {
    speed: clamp(Number(p.speed), 0, 2),
    warp: clamp(Number(p.warp), 0, 1),
    scale: clamp(Number(p.scale), 0.1, 3),
    complexity: clamp(Number(p.complexity), 0, 1),
    intensity: clamp(Number(p.intensity), 0, 2),
    rotation: clamp(Number(p.rotation), -1, 1),
    hueShift: clamp(Number(p.hueShift), 0, 1),
  };
  return {
    id: typeof o.id === 'string' ? o.id : crypto.randomUUID(),
    name: typeof o.name === 'string' ? o.name : 'Untitled',
    generator, palette, params,
    description: typeof o.description === 'string' ? o.description : undefined,
    ai: o.ai === true,
  };
}
